import { useEffect, useState } from 'react';
import { CampaignSelector } from '../CampaignSelector';
import { DaySelector } from '../DaySelector';
import { NetworkBreakdown } from '../NetworkBreakdown';
import { PeakPercentage } from '../PeakPercentage';
import {
  MONTH_LOWERCASE,
  MONTH_PASCAL,
  VARIABLE_OPTION
} from '../../constants';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { RootState, store } from '../../redux/store';
import { REDUX_KEY } from '../../redux/constants';
import {
  getMonthsBetweenDates,
  mapPascalMonthToLowercaseMonth
} from '../../util';
import { MonthProgress } from '../MonthProgress';
import styled from 'styled-components';
import COLORS from '../../colors';
import MonthNavigation from './MonthNavigation';
import { BottomNavigation } from '../BottomNavigation';
import {
  updateTotalCpm,
  updateTotalCpt,
  updateTotalLoading
} from '../../redux/features/calculated/calculatedSlice';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../../context/ModalContext';
import { FibLib } from '../FibLib';
import { setSelectedMonth } from '../../redux/features/campaignDetail/campaignDetailSlice';
import { AdMirroring } from '../AdMirroring';

const FIRST_STEP: number = 0;

const DISPLAY_NOTHING: null = null;

const StyledMonth = styled.span`
  color: ${COLORS.PRIMARY_600};
`;

/**
 * @todo when navigating to the next page, set the total loading in redux
 * @returns
 */
const VariablesSection = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { openModal, closeModal } = useModal();
  const { startDate, endDate } = useAppSelector(
    (state: RootState) => state.detail[REDUX_KEY.CAMPAIGN_LENGTH]
  );
  const { editMode } = useAppSelector((state: RootState) => state.option);
  const [months, setMonths] = useState<MONTH_PASCAL[] | undefined>(undefined);
  const [activeStep, setActiveStep] = useState<number>(0);
  const { calculated } = useAppSelector((state: RootState) => state);

  const mapDatesToMonthArray = () => {
    if (!startDate || !endDate) return;
    const selectedMonths = getMonthsBetweenDates(startDate, endDate);
    return selectedMonths;
  };

  const handleForwardNavigation = (): void => {
    if (!validate()) {
      displayInvalidDaysSelection();
      return;
    }

    if (activeStep < (months?.length || 0) - 1) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const validate = () => {
    const isAtLeastOneDaySelected = validateDays();
    return isAtLeastOneDaySelected;
  };

  const validateDays = () => {
    if (!months) return;

    let index = VARIABLE_OPTION.EDIT_ENTIRE_CAMPAIGN ? 0 : activeStep;

    const currentMonth = months[index];
    const lowercaseMonth = mapPascalMonthToLowercaseMonth(currentMonth);

    const days = calculated[lowercaseMonth].days;
    const hasSelectedDay = Object.values(days).some((value) => value === 0);

    if (!hasSelectedDay) {
      return false;
    }

    return true;
  };

  const handleBackwardNavigation = (): void => {
    if (!validate()) {
      displayInvalidDaysSelection();
      return;
    }

    if (activeStep > 0) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleNavigateNext = (): void => {
    const invalidMonths = getInvalidMonths();

    if (invalidMonths.length > 0) {
      openModal('ActionModal', {
        title: 'Error',
        content:
          'Please select at least one day for the following months: ' +
          invalidMonths.join(', '),
        cancelButtonText: 'Cancel',
        okButtonText: 'Ok',
        modalCallback: () => {
          closeModal();
        }
      });
      return;
    }

    openModal('ActionModal', {
      title: 'Continue?',
      content: 'Click continue navigate to the next section.',
      cancelButtonText: 'Cancel',
      okButtonText: 'Continue',
      modalCallback: () => {
        closeModal();
        navigate('/third');
        handleOnClick();
      }
    });
  };

  const getInvalidMonths = () => {
    if (!months) return [];

    let invalidMonths: MONTH_PASCAL[] = [];

    months.map((month) => {
      const lowercaseMonth = mapPascalMonthToLowercaseMonth(month);

      const isValid = Object.values(calculated[lowercaseMonth].days).some(
        (value) => value === 0
      );

      if (!isValid) {
        invalidMonths.push(month);
      }
    });

    return invalidMonths;
  };

  const displayInvalidDaysSelection = () => {
    openModal('ActionModal', {
      title: 'Error',
      content: 'Please select at least one day',
      cancelButtonText: 'Cancel',
      okButtonText: 'Ok',
      modalCallback: () => {
        closeModal();
      }
    });
  };

  const handleStepClick = (month: string) => {
    if (!months) {
      return;
    }

    if (!validate()) {
      displayInvalidDaysSelection();
      return;
    }

    const index = months.findIndex((element) => element === month);
    setActiveStep(index);
  };

  const goBackOneStep = (months: MONTH_PASCAL[]) => {
    return months[activeStep - 1];
  };

  const goForwardOneStep = (months: MONTH_PASCAL[]) => {
    return months[activeStep + 1];
  };

  const batchUpdateTotals = (
    month: MONTH_LOWERCASE,
    totalPeakLoading?: number,
    totalStationLoading?: number,
    totalDaysLoading?: number,
    totalFibLibLoading?: number,
    adMirroringLoading?: number
  ) => {
    const currentTotalPeakLoadingTotal =
      totalPeakLoading ?? store.getState().calculated[month].peakLoading;
    const currentStationLoadingTotal =
      totalStationLoading ??
      store.getState().calculated[month].stationLoading.loading;
    const currentDaysLoadingTotal =
      totalDaysLoading ?? store.getState().calculated[month].days.loading;
    const currentFibLibLoadingTotal =
      totalFibLibLoading ?? store.getState().calculated[month].fiblibLoading;

    let currentAdMirroringLoadingTotal = 0;

    if (adMirroringLoading !== 0 || adMirroringLoading !== undefined) {
      const isMirroringEnabled =
        store.getState().calculated[month].adMirroring.isAdMirroringEnabled;

      currentAdMirroringLoadingTotal = isMirroringEnabled
        ? store.getState().calculated[month].adMirroring.adMirroringLoading
        : 0;
    }

    const baseCpt = calculated[month].baseCPT;
    const baseCpm = calculated[month].baseCPM;
    const newTotalLoading =
      currentTotalPeakLoadingTotal +
      currentStationLoadingTotal +
      currentDaysLoadingTotal +
      currentFibLibLoadingTotal +
      currentAdMirroringLoadingTotal;
    const newTotalCpt = baseCpt * ((newTotalLoading + 100) / 100);
    const newTotalCpm = baseCpm * ((newTotalLoading + 100) / 100);

    dispatch(
      updateTotalLoading({
        totalLoading: newTotalLoading,
        month
      })
    );

    dispatch(
      updateTotalCpt({
        totalCpt: newTotalCpt,
        month
      })
    );

    dispatch(
      updateTotalCpm({
        totalCpm: newTotalCpm,
        month
      })
    );
  };

  useEffect(() => {
    if (!months || !editMode) return;

    dispatch(setSelectedMonth({ month: months[activeStep] }));
  }, [activeStep]);

  useEffect(() => {
    setMonths(mapDatesToMonthArray() as MONTH_PASCAL[]);
  }, [startDate, endDate]);

  const handleOnClick = () => {
    if (!validate()) {
      displayInvalidDaysSelection();
      return true;
    }

    let isError = false;
    return isError;
  };

  return (
    <>
      <CampaignSelector />

      {months && (
        <>
          {editMode === VARIABLE_OPTION.EDIT_ENTIRE_CAMPAIGN && (
            <>
              <h1>
                Editing{' '}
                <StyledMonth>
                  {months[FIRST_STEP]} - {months[months.length - 1]}
                </StyledMonth>
              </h1>
              <PeakPercentage
                key="peak-percentage"
                months={months}
                batchUpdateTotals={batchUpdateTotals}
              />
              <AdMirroring
                key="ad-mirroring-selector"
                months={months}
                batchUpdateTotals={(month, loading) =>
                  batchUpdateTotals(
                    month,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    loading
                  )
                }
              />
              <NetworkBreakdown
                key="network-breakdown"
                months={months}
                batchUpdateTotals={(month, loading) =>
                  batchUpdateTotals(month, undefined, loading, undefined)
                }
              />
              <DaySelector
                key="day-selector"
                months={months}
                batchUpdateTotals={(month, loading) =>
                  batchUpdateTotals(month, undefined, undefined, loading)
                }
              />
              <FibLib
                key="fib-lib-selector"
                months={months}
                batchUpdateTotals={(month, loading) =>
                  batchUpdateTotals(
                    month,
                    undefined,
                    undefined,
                    undefined,
                    loading
                  )
                }
              />
              <BottomNavigation
                nextTo="/third"
                onClick={handleOnClick}
                showBackNavigation={false}
              />
            </>
          )}

          {editMode === VARIABLE_OPTION.EDIT_MONTHS_INDIVIDUALLY && (
            <>
              <MonthProgress
                steps={months}
                activeStep={activeStep}
                onClick={handleStepClick}
              />
              <h1>
                Editing <StyledMonth>{months[activeStep]}</StyledMonth>
              </h1>
              <PeakPercentage
                key={`peak-percentage-${activeStep}`}
                months={[months[activeStep]]}
                batchUpdateTotals={batchUpdateTotals}
              />
              <AdMirroring
                key={`ad-mirroring-selector-${activeStep}`}
                months={[months[activeStep]]}
                batchUpdateTotals={(month, loading) =>
                  batchUpdateTotals(
                    month,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    loading
                  )
                }
              />
              <NetworkBreakdown
                key={`network-breakdown-${activeStep}`}
                months={[months[activeStep]]}
                batchUpdateTotals={(month, loading) =>
                  batchUpdateTotals(month, undefined, loading, undefined)
                }
              />
              <DaySelector
                key={`day-selector-${activeStep}`}
                months={[months[activeStep]]}
                batchUpdateTotals={(month, loading) =>
                  batchUpdateTotals(month, undefined, undefined, loading)
                }
              />
              <FibLib
                key={`fib-lib-${activeStep}`}
                months={[months[activeStep]]}
                batchUpdateTotals={(month, loading) =>
                  batchUpdateTotals(
                    month,
                    undefined,
                    undefined,
                    undefined,
                    loading
                  )
                }
              />
              <MonthNavigation
                previousMonth={
                  activeStep === FIRST_STEP
                    ? DISPLAY_NOTHING
                    : goBackOneStep(months)
                }
                nextMonth={
                  activeStep + 1 === months.length
                    ? DISPLAY_NOTHING
                    : goForwardOneStep(months)
                }
                showNextPageNavigation={activeStep + 1 === months.length}
                navigateForward={handleForwardNavigation}
                navigateBackward={handleBackwardNavigation}
                navigateNext={handleNavigateNext}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default VariablesSection;
